import { useState } from "react";
import { Form, Button, Table, Row, Col } from "react-bootstrap";
import { AppLoader } from "../../../../Components/common/AppLoader/AppLoader";
import { MdDelete, MdEdit, MdPlayArrow, MdVideocam } from "react-icons/md";
import { UPLOADS_BASE } from "../../constants/api";
import { getYoutubeThumbnailUrl, getYoutubeWatchUrl } from "../../utils/youtubeHelpers";

const EMPTY_FORM = {
  media_date: "",
  place: "",
  short_note: "",
  media_link: "",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const MediaMetaFields = ({ form, onChange }) => (
  <>
    <Row className="em-row">
      <Form.Group as={Col} md="6">
        <Form.Label>Date</Form.Label>
        <Form.Control
          type="date"
          value={form.media_date}
          onChange={(e) => onChange("media_date", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6">
        <Form.Label>Place</Form.Label>
        <Form.Control
          type="text"
          value={form.place}
          onChange={(e) => onChange("place", e.target.value)}
          placeholder="Location"
        />
      </Form.Group>
    </Row>
    <Row className="em-row em-row--last">
      <Form.Group as={Col} md="12">
        <Form.Label>Short Note</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={form.short_note}
          onChange={(e) => onChange("short_note", e.target.value)}
          placeholder="Brief description"
        />
      </Form.Group>
    </Row>
  </>
);

export const PictureVideoGalleryScreen = ({
  mediaGallery,
  loading,
  saving,
  onSave,
  onDelete,
}) => {
  const images = mediaGallery.filter((m) => m.media_type === "Image");
  const videos = mediaGallery.filter((m) => m.media_type === "Video");

  const [imageForm, setImageForm] = useState(EMPTY_FORM);
  const [videoForm, setVideoForm] = useState(EMPTY_FORM);
  const [imageFiles, setImageFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingType, setEditingType] = useState(null);

  const resetEdit = () => {
    setEditingId(null);
    setEditingType(null);
    setImageForm(EMPTY_FORM);
    setVideoForm(EMPTY_FORM);
    setImageFiles([]);
  };

  const handleImageChange = (field, value) => {
    setImageForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleVideoChange = (field, value) => {
    setVideoForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageFilesChange = (fileList) => {
    setImageFiles(Array.from(fileList || []));
  };

  const startEdit = (item) => {
    const nextForm = {
      media_date: item.media_date ? String(item.media_date).slice(0, 10) : "",
      place: item.place || "",
      short_note: item.short_note || "",
      media_link: item.media_link || "",
    };
    setEditingId(item.media_id);
    setEditingType(item.media_type);
    setImageFiles([]);
    if (item.media_type === "Image") {
      setImageForm(nextForm);
    } else {
      setVideoForm(nextForm);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    const ok = await onSave(
      { ...imageForm, media_type: "Image" },
      imageFiles,
      editingType === "Image" ? editingId : null
    );
    if (ok) {
      setImageForm(EMPTY_FORM);
      setImageFiles([]);
      if (editingType === "Image") resetEdit();
    }
  };

  const handleVideoSave = async (e) => {
    e.preventDefault();
    const ok = await onSave(
      { ...videoForm, media_type: "Video" },
      null,
      editingType === "Video" ? editingId : null
    );
    if (ok) {
      setVideoForm(EMPTY_FORM);
      if (editingType === "Video") resetEdit();
    }
  };

  if (loading) {
    return <AppLoader message="Loading gallery…" variant="portal" />;
  }

  return (
    <div className="media-gallery-page">
      <div className="em-section-card">
        <h4 className="em-section-title">Picture Gallery</h4>

        <Form onSubmit={handleImageUpload}>
          <Row className="em-row em-row--last">
            <Form.Group as={Col} md="12" controlId="galleryImagesUpload">
              <Form.Label>Upload Images</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple={!editingId || editingType !== "Image"}
                className="em-file-input"
                key={editingType === "Image" ? `edit-${editingId}` : "new-images"}
                onChange={(e) => handleImageFilesChange(e.target.files)}
                required={!editingId && imageFiles.length === 0}
              />
              <span className="em-field-hint">
                Select one or more images for the hospital gallery
              </span>
            </Form.Group>
          </Row>

          {imageFiles.length > 0 && (
            <div className="em-gallery-preview media-gallery-selected-preview">
              {imageFiles.map((file) => (
                <div key={`${file.name}-${file.lastModified}`} className="em-gallery-thumb">
                  <img src={URL.createObjectURL(file)} alt={file.name} />
                </div>
              ))}
            </div>
          )}

          <MediaMetaFields form={imageForm} onChange={handleImageChange} />

          <div className="media-gallery-form__actions">
            {editingType === "Image" && (
              <Button type="button" variant="outline-secondary" onClick={resetEdit} disabled={saving}>
                Cancel Edit
              </Button>
            )}
            <Button type="submit" variant="primary" className="btn-submit" disabled={saving}>
              {saving
                ? "Uploading…"
                : editingType === "Image"
                  ? "Update Image"
                  : "Upload Images"}
            </Button>
          </div>
        </Form>

        {images.length > 0 && (
          <div className="media-gallery-uploaded">
            <h5 className="media-gallery-uploaded__title">Uploaded Pictures</h5>
            <div className="hm-gallery">
              {images.map((item) => (
                <div key={item.media_id} className="hm-gallery-item media-gallery-item">
                  <img
                    src={`${UPLOADS_BASE}/uploads/${item.media_upload}`}
                    alt={item.short_note || "Gallery image"}
                  />
                  <div className="media-gallery-item__meta">
                    {item.place && <span>{item.place}</span>}
                    {item.media_date && <span>{formatDate(item.media_date)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="em-section-card">
        <h4 className="em-section-title">
          <MdVideocam className="media-gallery-title-icon" /> Video Gallery
        </h4>

        <Form onSubmit={handleVideoSave}>
          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="galleryVideoLink">
              <Form.Label>Video Link (URL)</Form.Label>
              <Form.Control
                type="url"
                value={videoForm.media_link}
                onChange={(e) => handleVideoChange("media_link", e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
              <span className="em-field-hint">
                Add a YouTube or video link to show in the video gallery
              </span>
            </Form.Group>
          </Row>

          <MediaMetaFields form={videoForm} onChange={handleVideoChange} />

          <div className="media-gallery-form__actions">
            {editingType === "Video" && (
              <Button type="button" variant="outline-secondary" onClick={resetEdit} disabled={saving}>
                Cancel Edit
              </Button>
            )}
            <Button type="submit" variant="primary" className="btn-submit" disabled={saving}>
              {saving
                ? "Saving…"
                : editingType === "Video"
                  ? "Update Video"
                  : "Add Video"}
            </Button>
          </div>
        </Form>

        {videos.length > 0 && (
          <div className="media-gallery-uploaded">
            <h5 className="media-gallery-uploaded__title">Uploaded Videos</h5>
            <div className="hm-gallery hm-gallery--videos">
              {videos.map((item) => {
                const thumb = getYoutubeThumbnailUrl(item.media_link);
                const watchUrl = getYoutubeWatchUrl(item.media_link);
                return (
                  <div key={item.media_id} className="hm-gallery-item media-gallery-item media-gallery-item--video">
                    {thumb ? (
                      <a href={watchUrl || item.media_link} target="_blank" rel="noopener noreferrer">
                        <img src={thumb} alt={item.short_note || "Video"} />
                        <span className="media-gallery-item__play">
                          <MdPlayArrow size={32} />
                        </span>
                      </a>
                    ) : (
                      <div className="media-gallery-item__link-fallback">
                        <MdPlayArrow size={32} />
                      </div>
                    )}
                    <div className="media-gallery-item__meta">
                      {item.place && <span>{item.place}</span>}
                      {item.media_date && <span>{formatDate(item.media_date)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="em-section-card media-gallery-table-card">
        <h4 className="em-section-title">Media Gallery Records</h4>
        {mediaGallery.length === 0 ? (
          <p className="hm-card-text hm-card-text--muted">No pictures or videos added yet.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="media-gallery-table">
              <thead>
                <tr>
                  <th>Media ID</th>
                  <th>Media Type</th>
                  <th>Media Upload / Link</th>
                  <th>Date</th>
                  <th>Place</th>
                  <th>Short Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mediaGallery.map((item) => (
                  <tr key={item.media_id}>
                    <td>{item.media_id}</td>
                    <td>{item.media_type}</td>
                    <td className="media-gallery-table__link">
                      {item.media_type === "Image"
                        ? item.media_upload
                        : item.media_link}
                    </td>
                    <td>{formatDate(item.media_date)}</td>
                    <td>{item.place || "—"}</td>
                    <td>{item.short_note || "—"}</td>
                    <td>
                      <div className="media-gallery-table__actions">
                        <button
                          type="button"
                          className="media-gallery-table__btn"
                          onClick={() => startEdit(item)}
                          aria-label="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          type="button"
                          className="media-gallery-table__btn media-gallery-table__btn--danger"
                          onClick={() => onDelete(item.media_id)}
                          aria-label="Delete"
                          disabled={saving}
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PictureVideoGalleryScreen;
